import { Container, FederatedPointerEvent, Graphics, Point, Sprite, Texture } from "pixi.js";
import { ButtonWithScaleAnimation } from "./ButtonWithScaleAnimation";
import { Event } from "event-system";
import { UserProfileResourcePackage } from "../../popups/userprofile/resources/UserProfileResourcePackage";

export class Avatar extends Container {
    private static readonly POSITION_TO_LEADERBOARD_ICON: Map<number, keyof UserProfileResourcePackage> = new Map([
        [1, "assets_views_user_profile_leaderboard_golden_png"],
        [2, "assets_views_user_profile_leaderboard_silver_png"],
        [3, "assets_views_user_profile_leaderboard_bronze_png"]
    ]);

    private readonly userProfileResources: UserProfileResourcePackage;

    private profile: ButtonWithScaleAnimation;
    private circleMask: Graphics;
    private whiteStroke: Graphics;
    private profileGlow: Sprite;
    private trophyIcon: Sprite;

    public get onProfileButtonClick(): Event<ButtonWithScaleAnimation, FederatedPointerEvent> {
        return this.profile.onUp;
    }

    public set glow(value: boolean) {
        this.profileGlow.visible = value;
    }

    public set avatarSize(value: Point) {
        this.profile.defaultView.width = this.profileGlow.width = this.whiteStroke.width = this.circleMask.width = value.x;
        this.profile.defaultView.height = this.profileGlow.height = this.whiteStroke.height = this.circleMask.height = value.y;
        this.trophyIcon.position.set(0, this.profile.height - this.trophyIcon.height);
        this.profileGlow.position.set(value.x - this.height, value.y - this.height);
    }

    public set trophySize(value: Point) {
        this.trophyIcon.width = value.x;
        this.trophyIcon.height = value.y;
        this.trophyIcon.position.set(0, this.profile.height - this.trophyIcon.height);
    }

    public set trophy(value: boolean) {
        this.trophyIcon.visible = value;
    }

    public set enabled(value: boolean) {
        this.eventMode = value ? "static" : "none";
    }

    public get enabled(): boolean {
        return this.eventMode === "static";
    }

    public constructor(userProfileResources: UserProfileResourcePackage, avatarTexture: Texture) {
        super();
        this.userProfileResources = userProfileResources;
        this.build(avatarTexture);
        this.glow = false;
    }

    public updateTexture(texture: Texture): void {
        this.profile.defaultView.texture = texture;
    }

    public updateLeaderboardIcon(position: number): void {
        const textureProperty = Avatar.POSITION_TO_LEADERBOARD_ICON.get(position);
        if (textureProperty == null) {
            this.trophyIcon.texture = null;
            return;
        }
        this.trophyIcon.texture = this.userProfileResources[textureProperty] as Texture;
    }

    private build(avatarTexture: Texture): void {
        this.profile = new ButtonWithScaleAnimation(avatarTexture);

        const minSize = Math.min(this.profile.width * 0.5, this.profile.height * 0.5);
        const strokeSize = Math.floor(minSize * 0.13);

        this.whiteStroke = new Graphics();
        this.whiteStroke.lineStyle({ width: strokeSize, color: 0xffffff });
        this.whiteStroke.drawCircle((this.profile.width + strokeSize * 0.5) * 0.5, (this.profile.height + strokeSize * 0.5) * 0.5, minSize);
        this.profile.addChild(this.whiteStroke);

        this.circleMask = new Graphics();
        this.circleMask.beginFill(0xffffff);
        this.circleMask.drawCircle((this.profile.width + strokeSize * 0.5) * 0.5, (this.profile.height + strokeSize * 0.5) * 0.5, minSize + strokeSize);
        this.circleMask.endFill();
        this.profile.defaultView.mask = this.circleMask;
        this.profile.addChild(this.circleMask);

        this.profileGlow = new Sprite(this.userProfileResources.assets_views_user_profile_avatar_glow_png);
        this.profileGlow.width = this.profile.width;
        this.profileGlow.height = this.profile.height;
        this.profile.addChild(this.profileGlow);

        this.trophyIcon = new Sprite();
        this.trophyIcon.position.set(0, this.profile.height - this.trophyIcon.height);

        this.addChild(
            this.profile,
            this.trophyIcon
        );
    }
}
